import { useState, useEffect } from 'react'
import { useAdminHighlights } from '@/hooks/useAdmin'

export default function HighlightsManager() {
  const { highlights, loading, fetchHighlights, createHighlight, deleteHighlight } = useAdminHighlights()
  const [showForm, setShowForm] = useState(false)
  const [newsId, setNewsId] = useState('')
  const [type, setType] = useState('featured')
  const [category, setCategory] = useState('')
  const [displayOrder, setDisplayOrder] = useState(1)
  const [actionLoading, setActionLoading] = useState(false)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 3000)
  }

  useEffect(() => {
    fetchHighlights()
  }, [])

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newsId) return
    setActionLoading(true)
    const result = await createHighlight({ news_id: newsId, type, category: category || undefined, display_order: displayOrder })
    if (result) {
      showToast('Destaque criado!')
      setShowForm(false)
      setNewsId('')
      setCategory('')
      setDisplayOrder(1)
    } else {
      showToast('Erro ao criar destaque', 'error')
    }
    setActionLoading(false)
    fetchHighlights()
  }

  const handleDelete = async (id: string) => {
    setActionLoading(true)
    const success = await deleteHighlight(id)
    if (success) {
      showToast('Destaque removido')
    } else {
      showToast('Erro ao remover', 'error')
    }
    setActionLoading(false)
    fetchHighlights()
  }

  const typeLabels: Record<string, string> = {
    headline: 'Manchete',
    featured: 'Destaque',
    breaking: 'Breaking News',
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-alvinegro-muted">Gerencie os destaques exibidos na página inicial</p>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 bg-alvinegro-black text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-alvinegro-dark transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line"></i>
          Novo Destaque
        </button>
      </div>

      <div className="bg-white rounded-xl border border-alvinegro-border overflow-hidden">
        {loading ? (
          <div className="p-8 space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 animate-pulse">
                <div className="w-10 h-10 rounded bg-alvinegro-light" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 bg-alvinegro-light rounded w-1/3" />
                  <div className="h-3 bg-alvinegro-light rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-alvinegro-border bg-alvinegro-light">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-alvinegro-muted uppercase tracking-wider">Tipo</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-alvinegro-muted uppercase tracking-wider">Notícia</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-alvinegro-muted uppercase tracking-wider hidden md:table-cell">Categoria</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-alvinegro-muted uppercase tracking-wider">Ordem</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-alvinegro-muted uppercase tracking-wider">Ações</th>
                </tr>
              </thead>
              <tbody>
                {highlights.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-12 text-center text-alvinegro-muted">
                      <i className="ri-star-line text-4xl mb-3 block"></i>
                      <p className="text-sm">Nenhum destaque configurado</p>
                    </td>
                  </tr>
                ) : (
                  highlights.map((h) => (
                    <tr key={h.id} className="border-b border-alvinegro-border hover:bg-alvinegro-light/50 transition-colors">
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs border ${
                          h.type === 'headline' ? 'bg-alvinegro-gold/10 text-yellow-700 border-yellow-200' :
                          h.type === 'breaking' ? 'bg-red-50 text-red-600 border-red-200' :
                          'bg-alvinegro-light text-alvinegro-dark border-alvinegro-border'
                        }`}>
                          {typeLabels[h.type] || h.type}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-alvinegro-black">{h.news_title || h.news_id.substring(0, 8)}</p>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span className="text-xs text-alvinegro-muted">{h.category || '-'}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-alvinegro-dark font-medium">{h.display_order || 0}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleDelete(h.id)}
                            disabled={actionLoading}
                            className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-red-50 text-red-600 transition-colors cursor-pointer"
                            title="Remover"
                          >
                            <i className="ri-delete-bin-line text-sm"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-2xl p-6 md:p-8 max-w-md w-full">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-serif font-bold text-xl text-alvinegro-black">Novo Destaque</h3>
              <button onClick={() => setShowForm(false)} className="w-8 h-8 rounded-full bg-alvinegro-light flex items-center justify-center cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-alvinegro-dark mb-1">ID da Notícia (UUID)</label>
                <input
                  value={newsId}
                  onChange={(e) => setNewsId(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg border border-alvinegro-border text-sm focus:outline-none focus:border-alvinegro-black"
                  placeholder="00000000-0000-0000-0000-000000000000"
                  required
                />
                <p className="text-xs text-alvinegro-muted mt-1">Cole o UUID da notícia que deseja destacar</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-alvinegro-dark mb-1">Tipo</label>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg border border-alvinegro-border text-sm bg-white focus:outline-none focus:border-alvinegro-black"
                >
                  <option value="featured">Destaque</option>
                  <option value="headline">Manchete</option>
                  <option value="breaking">Breaking News</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-alvinegro-dark mb-1">Categoria</label>
                <input
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg border border-alvinegro-border text-sm focus:outline-none focus:border-alvinegro-black"
                  placeholder="Opcional"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-alvinegro-dark mb-1">Ordem de exibição</label>
                <input
                  type="number"
                  value={displayOrder}
                  onChange={(e) => setDisplayOrder(Number(e.target.value))}
                  className="w-full px-4 py-2 rounded-lg border border-alvinegro-border text-sm focus:outline-none focus:border-alvinegro-black"
                  min={1}
                />
              </div>
              <div className="flex items-center gap-3 pt-4">
                <button
                  type="submit"
                  disabled={actionLoading}
                  className="bg-alvinegro-black text-white px-6 py-2.5 rounded-full text-sm font-medium hover:bg-alvinegro-dark transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  {actionLoading ? 'Criando...' : 'Criar Destaque'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-6 py-2.5 rounded-full border border-alvinegro-border text-sm font-medium text-alvinegro-dark hover:bg-alvinegro-light transition-colors cursor-pointer"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50">
          <div className={`rounded-xl px-5 py-3 shadow-lg border text-sm font-medium flex items-center gap-2 ${toast.type === 'success' ? 'bg-white border-green-200 text-green-700' : 'bg-white border-red-200 text-red-600'}`}>
            <i className={`${toast.type === 'success' ? 'ri-check-double-line' : 'ri-error-warning-line'}`}></i>
            {toast.message}
          </div>
        </div>
      )}
    </div>
  )
}